import { useEffect } from 'react'
import { token } from '@/lib/token'

/*
 * The "missing layer between data and distribution" beam diagram.
 *
 * Reproduces the capture's own inline script. It draws ten quadratic Bézier
 * paths — five input circles into the centre icon, then the centre out to five
 * output circles — and lays a short travelling dash over each one.
 *
 * The original ran on `$(window).on('load')`, which never fires again under
 * client-side routing, so the section rendered as bare circles with no lines.
 *
 * `.beam-path`, `.beam-segment` and the `beamMove` keyframe come from the
 * page's own inline CSS, ported to src/pages/css/home.css.
 */

const SVG_NS = 'http://www.w3.org/2000/svg'

/** Colours the original picks from at random. */
/* --color-blue / --color-purple / --color-magenta, read at run time: the beams
 * are SVG strokes set from JS, which cannot take a `var()`. */
const COLOR_TOKENS = ['--color-blue', '--color-purple', '--color-magenta'] as const

/** Curve offsets, copied from the capture. */
const CONNECTIONS: Array<{ from: string; to: string; curve: number }> = [
  { from: '#drive', to: '#openai', curve: -60 },
  { from: '#docs', to: '#openai', curve: -40 },
  { from: '#whatsapp', to: '#openai', curve: -15 },
  { from: '#messenger', to: '#openai', curve: 15 },
  { from: '#notion', to: '#openai', curve: 40 },
  { from: '#openai', to: '#user1', curve: -40 },
  { from: '#openai', to: '#user2', curve: -20 },
  { from: '#openai', to: '#user3', curve: 0 },
  { from: '#openai', to: '#user4', curve: 20 },
  { from: '#openai', to: '#user5', curve: 40 },
]

function centerWithin(el: Element, parent: Element) {
  const a = el.getBoundingClientRect()
  const b = parent.getBoundingClientRect()
  return { x: a.left - b.left + a.width / 2, y: a.top - b.top + a.height / 2 }
}

export function BeamLines() {
  useEffect(() => {
    const draw = () => {
      const wrapper = document.getElementById('beam-wrapper')
      const svg = document.querySelector<SVGSVGElement>('.beam-lines')
      if (!wrapper || !svg) return false

      // Circles are laid out by their images; before those load every centre
      // collapses to the same point and the paths come out as dots.
      if (wrapper.getBoundingClientRect().width === 0) return false

      svg.replaceChildren()
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      for (const { from, to, curve } of CONNECTIONS) {
        const a = document.querySelector(from)
        const b = document.querySelector(to)
        if (!a || !b) continue

        const start = centerWithin(a, wrapper)
        const end = centerWithin(b, wrapper)

        // The original flips any path that would run right-to-left, so every
        // curve bends the same way regardless of source order.
        const rightToLeft = start.x > end.x
        const p1 = rightToLeft ? end : start
        const p2 = rightToLeft ? start : end
        const midX = (p1.x + p2.x) / 2
        const midY = (p1.y + p2.y) / 2 + curve
        const d = `M ${p1.x},${p1.y} Q ${midX},${midY} ${p2.x},${p2.y}`

        const path = document.createElementNS(SVG_NS, 'path')
        path.setAttribute('d', d)
        path.classList.add('beam-path')
        svg.appendChild(path)

        if (reduceMotion) continue

        // The travelling dash. Speed and dash length are the capture's.
        const measure = document.createElementNS(SVG_NS, 'path')
        measure.setAttribute('d', d)
        const pathLength = measure.getTotalLength()
        const beamLength = 20
        const speed = 0.1 // px per ms
        const duration = pathLength / speed

        const beam = document.createElementNS(SVG_NS, 'path')
        beam.setAttribute('d', d)
        beam.classList.add('beam-segment')
        beam.style.stroke = token(COLOR_TOKENS[Math.floor(Math.random() * COLOR_TOKENS.length)])
        beam.style.strokeDasharray = `${beamLength} ${pathLength}`
        beam.style.strokeDashoffset = '0'
        beam.style.animation = `beamMove ${duration}ms linear infinite`
        beam.style.animationDelay = `${Math.random() * 2000}ms`
        svg.appendChild(beam)
      }
      return true
    }

    let frame = 0
    let attempts = 0
    // The original ran on window.load, once the circle images had sized their
    // containers. Retry until the wrapper has a real width.
    const attempt = () => {
      if (draw() || attempts++ > 40) return
      frame = window.setTimeout(attempt, 150)
    }
    attempt()

    let resizeFrame = 0
    const onResize = () => {
      cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(draw)
    }
    window.addEventListener('resize', onResize)

    // Redraw once late-loading circle images settle the layout.
    const imgs = Array.from(document.querySelectorAll('#beam-wrapper img'))
    const onImg = () => onResize()
    for (const img of imgs) img.addEventListener('load', onImg)

    return () => {
      window.clearTimeout(frame)
      cancelAnimationFrame(resizeFrame)
      window.removeEventListener('resize', onResize)
      for (const img of imgs) img.removeEventListener('load', onImg)
    }
  }, [])

  return null
}
